<div class="mb-3">
  <button type="button" class="btn btn-link ps-0 back-link" (click)="onBackClicked()">
    <span aria-hidden="true">&larr;</span>
    <span i18n="@@catalogItemDetail.back">Zurück zu den Ergebnissen</span>
  </button>

  @if (loading) {
    <div class="d-flex justify-content-center py-5">
      <app-loading-spinner
        text="Artikel wird geladen…"
        i18n-text="@@catalogItemDetail.loading"
      ></app-loading-spinner>
    </div>
  } @else if (!catalogSku) {
    <div class="alert alert-warning" role="alert" i18n="@@catalogItemDetail.notFound">
      Dieser Artikel ist nicht verfügbar oder existiert nicht mehr.
    </div>
  } @else {
    <div class="row g-4">
      <div class="col-md-6">
        @if (images.length > 0) {
          <!-- No data-bs-ride: its auto-init only runs on window load and would
               miss this carousel, which Angular renders after the sku arrives.
               The controls and indicators work regardless — Bootstrap binds
               those through delegated document handlers. Autoplay is unwanted
               for a product gallery anyway. -->
          <div id="catalogItemCarousel" class="carousel carousel-dark slide item-gallery">
            @if (images.length > 1) {
              <div class="carousel-indicators">
                @for (image of images; track $index) {
                  <button
                    type="button"
                    data-bs-target="#catalogItemCarousel"
                    [attr.data-bs-slide-to]="$index"
                    [class.active]="$first"
                    [attr.aria-current]="$first ? 'true' : null"
                    [attr.aria-label]="'Bild ' + ($index + 1)"
                  ></button>
                }
              </div>
            }

            <div class="carousel-inner">
              @for (image of images; track $index) {
                <div class="carousel-item" [class.active]="$first">
                  <!-- A button, not a bare img with a click handler, so the
                       lightbox is reachable by keyboard. The slide's own $index
                       is the one the user clicked, which saves asking Bootstrap
                       which slide is currently showing — Angular does not track
                       that. -->
                  <button
                    type="button"
                    class="item-gallery__trigger"
                    data-bs-toggle="modal"
                    data-bs-target="#catalogItemLightbox"
                    (click)="openLightbox($index)"
                    [attr.aria-label]="'Bild ' + ($index + 1) + ' vergrößern'"
                  >
                    <img
                      [src]="image.mediumImageUrl"
                      [alt]="catalogSku.skuName1"
                      class="d-block w-100 item-gallery__image"
                    />
                  </button>
                </div>
              }
            </div>

            @if (images.length > 1) {
              <button
                class="carousel-control-prev"
                type="button"
                data-bs-target="#catalogItemCarousel"
                data-bs-slide="prev"
              >
                <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                <span class="visually-hidden" i18n="@@catalogItemDetail.carousel.prev">
                  Vorheriges Bild
                </span>
              </button>
              <button
                class="carousel-control-next"
                type="button"
                data-bs-target="#catalogItemCarousel"
                data-bs-slide="next"
              >
                <span class="carousel-control-next-icon" aria-hidden="true"></span>
                <span class="visually-hidden" i18n="@@catalogItemDetail.carousel.next">
                  Nächstes Bild
                </span>
              </button>
            }
          </div>
        } @else {
          <div class="item-gallery item-gallery--empty"></div>
        }
      </div>

      <div class="col-md-6">
        <div class="item-title-row">
          <!-- backend #690: see app-catalog-item-card. -->
          <h1 class="item-title" [attr.lang]="contentLanguage">{{ catalogSku.skuName1 }}</h1>

          <!-- #652: second trigger for the Merkliste, same icon and states as on the results
               card. Hidden for an anonymous visitor — this page is public, the Merkliste API
               is not (decision 3a). Unlike the order action below it is NOT gated on
               availability: a listing that is out of stock today is exactly the one worth
               remembering. -->
          @if (canBookmark) {
            <button
              type="button"
              class="bookmark-toggle"
              data-testid="catalog-item-detail-bookmark-button"
              [asyncClick]="bookmarkAction(catalogSku.skuId)"
              #bookmarkButton="asyncClick"
              [disabled]="bookmarkButton.disabled"
              [attr.aria-pressed]="isBookmarked"
              [attr.aria-label]="bookmarkLabel"
              [attr.title]="bookmarkLabel"
              [attr.data-bookmarked]="isBookmarked"
            >
              <fa-icon [icon]="bookmarkIcon"></fa-icon>
            </button>
          }
        </div>

        <div class="item-price">
          <span class="item-price__value">{{ catalogSku.catalogGrossPrice | currency: catalogSku.currency }}</span>
          <span class="item-price__note">{{ catalogSku | priceNote }}</span>
        </div>

        <!-- #509: non-orderable listings stay visible with an explicit state badge.
             Anything that is not explicitly 'available' (including an absent or
             unrecognised state) gets the neutral badge, so the badge never contradicts
             the suppressed order action below. -->
        @if (isAvailableAtSource) {
          <span class="badge text-bg-info mb-3" i18n="@@catalog.availability.atSource">
            Beim Anbieter erhältlich
          </span>
        } @else if (availability === 'unavailable' && catalogSku.isImported) {
          <!-- #562 (decision 5): „Zurzeit nicht verfügbar" would read as if OUR stock had run
               out. For an imported listing we know something narrower and can say it. Only the
               explicitly-unavailable state qualifies — an absent or unrecognised one still
               falls through to the neutral badge below, which claims nothing. -->
          <span class="badge text-bg-secondary mb-3" i18n="@@catalog.availability.unavailableAtSource">
            Beim Anbieter derzeit nicht verfügbar
          </span>
        } @else if (availability !== 'available') {
          <span class="badge text-bg-secondary mb-3" i18n="@@catalog.availability.unavailable">
            Zurzeit nicht verfügbar
          </span>
        }

        <dl class="item-meta">
          <dt i18n="@@catalogItemDetail.seller.label">Anbieter</dt>
          <dd>{{ sellerLabel }}: {{ catalogSku.sellerFullName }}</dd>

          @if (catalogSku.skuDescription) {
            <dt i18n="@@catalogItemDetail.description.label">Beschreibung</dt>
            <dd class="item-description" [attr.lang]="contentLanguage">{{ catalogSku.skuDescription }}</dd>
          }
        </dl>

        <!-- Backend #562: an imported listing is fulfilled by the shop it was scanned
             from, not by us, so it offers the way there instead of the order action.
             Keyed off the URL rather than `isImported` (decisions 1/4) — an imported
             listing without a usable URL renders no link rather than a homepage
             fallback, and still no cart, because its availability is never 'available'.
             Deliberately not gated on `profile?.customer`: an outbound link is not an
             order action, so an anonymous visitor may follow it too. -->
        @if (shopUrl) {
          <a class="btn btn-primary" [href]="shopUrl" target="_blank" rel="noopener noreferrer"
             i18n="@@catalog.toShop">Zum Shop</a>
        } @else if (profile?.customer && availability === 'available') {
          <!-- Only offer the order action to a signed-in customer, and only for an
               explicitly orderable listing (#562 decision 3 — an absent or unrecognised
               availability counts as not orderable). -->
          <app-add-to-cart [catalogSku]="catalogSku"></app-add-to-cart>
        }
      </div>
    </div>

    @if (images.length > 0) {
      <!-- Deliberately not a second Bootstrap carousel: that would leave the
           active slide owned by Bootstrap's JS while Angular re-renders the
           same classes, and the two would fight over the DOM. Here Angular
           owns `lightboxIndex` outright and Bootstrap only opens and closes
           the modal (delegated data-api, so no manual init needed). -->
      <div
        id="catalogItemLightbox"
        class="modal fade item-lightbox"
        tabindex="-1"
        aria-hidden="true"
        [attr.aria-label]="catalogSku.skuName1"
        (keydown.arrowleft)="showPreviousImage()"
        (keydown.arrowright)="showNextImage()"
      >
        <div class="modal-dialog modal-fullscreen">
          <div class="modal-content item-lightbox__content">
            <div class="modal-header border-0">
              <span class="item-lightbox__counter">
                {{ lightboxIndex + 1 }} / {{ images.length }}
              </span>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Schließen"
                i18n-aria-label="@@catalogItemDetail.lightbox.close"
              ></button>
            </div>

            <div class="modal-body item-lightbox__body">
              @if (images.length > 1) {
                <button
                  type="button"
                  class="item-lightbox__nav item-lightbox__nav--prev"
                  (click)="showPreviousImage()"
                >
                  <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                  <span class="visually-hidden" i18n="@@catalogItemDetail.carousel.prev">
                    Vorheriges Bild
                  </span>
                </button>
              }

              <img
                [src]="images[lightboxIndex].largeImageUrl"
                [alt]="catalogSku.skuName1"
                class="item-lightbox__image"
              />

              @if (images.length > 1) {
                <button
                  type="button"
                  class="item-lightbox__nav item-lightbox__nav--next"
                  (click)="showNextImage()"
                >
                  <span class="carousel-control-next-icon" aria-hidden="true"></span>
                  <span class="visually-hidden" i18n="@@catalogItemDetail.carousel.next">
                    Nächstes Bild
                  </span>
                </button>
              }
            </div>
          </div>
        </div>
      </div>
    }
  }
</div>
